import {css} from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {CSSGridTrackSize} from '../../types/CSSGridTrackSize';
import {FlexboxAlign} from '../../types/Flexbox';
import {GridAutoFlow} from '../../types/GridAutoFlow';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {getSize} from '../../utils/getSize';

export interface GridProps extends TestIdProps {
  columns: number | ValueByDevice<number>;
  spacing?: Integer | ValueByDevice<Integer>;
  grow?: number;
  verticalSpacing?: Integer | ValueByDevice<Integer>;
  horizontalSpacing?: Integer | ValueByDevice<Integer>;
  children?: ReactNode;
  minHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  align?: FlexboxAlign;
  autoRows?: CSSGridTrackSize | ValueByDevice<CSSGridTrackSize>;
  autoFlow?: GridAutoFlow;
}

export function Grid(props: GridProps) {
  const columns = useResponsivePropValue(props.columns);
  const spacing = useResponsivePropValue(props.spacing);
  const verticalSpacing = useResponsivePropValue(props.verticalSpacing);
  const horizontalSpacing = useResponsivePropValue(props.horizontalSpacing);
  const minHeight = useResponsivePropValue(props.minHeight);
  const autoRows = useResponsivePropValue(props.autoRows);

  const verticalGap = verticalSpacing ?? spacing ?? DEFAULT_GAP;
  const horizontalGap = horizontalSpacing ?? spacing ?? DEFAULT_GAP;

  return (
    <div
      data-testid={props['data-testid']}
      css={css`
        display: grid;
        grid-template-columns: repeat(${columns}, minmax(0, 1fr));
        grid-auto-rows: ${autoRows ?? 'initial'};
        grid-auto-flow: ${props.autoFlow ?? 'initial'};
        align-items: ${props.align ?? 'initial'};
        flex-grow: ${props.grow ?? 'initial'};
        gap: ${getSize(verticalGap)} ${getSize(horizontalGap)};
        min-width: 0;
        min-height: ${isNotNil(minHeight) ? getCssSize(minHeight) : 0};
      `}
    >
      {props.children}
    </div>
  );
}

const DEFAULT_GAP = 4;
